import { useEffect, useMemo, useState, KeyboardEvent } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import BaseCard from '@/components/ui/BaseCard';
import logo from '@/assets/images/AURA_logo.png';
import BaseAlert from '@/components/feedback/BaseAlert';
import BaseTextField from '@/components/form/BaseTextField';
import BaseButton from '@/components/ui/BaseButton';
import { AccountCircle, Check, Close, Lock } from '@mui/icons-material';
import { showLoading, hideLoading } from '@/components/ui/LoadingOverlay';
import { resetPasswordAPI } from '@/api/reset';
import { useNavigate } from 'react-router-dom';
import localforage from 'localforage';
import { decodeToken } from '@/utils/tokenUtils';
import BaseDialog from '@/components/ui/BaseDialog';
import { useTranslation } from 'react-i18next';
import PasswordField from '@/components/form/PasswordField';

export default function ResetPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [disableUI, setDisableUI] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [toggleExpiredDialog, setToggleExpiredDialog] = useState(false);
    const [form, setForm] = useState({
        username: '',
        newPassword: '',
    });

    // Hook
    useEffect(() => {
        const pw = form.newPassword;
        setPwdValidation({
            lower: /[a-z]/.test(pw),
            upper: /[A-Z]/.test(pw),
            number: /\d/.test(pw),
            special: /[!@#$%^&*()\-_=+\\[\]{}\\|;:'",.<>/?`~]/.test(pw),
            length: pw.length >= 8,
            maxLength: pw.length <= 70,
            noUsername: form.username ? !pw.includes(form.username) : true,
        });
    }, [form.newPassword, form.username]);

    // Main Logic
    const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };
    const enterKeyHandler = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    };
    const handleSubmit = async () => {
        setShowAlert(false);
        if (disableUI || toggleResetDisable) return;
        if (!form.username || !form.newPassword) {
            setShowAlert(true);
            setErrorMessage(t('All fields are required.'));
            return;
        }
        try {
            setDisableUI(true);
            showLoading();
            setErrorMessage(null);
            setSuccessMessage(null);
            const recovery_token = await localforage.getItem('recovery_token');
            const {
                data: { isSuccess, status, message, data },
            } = await resetPasswordAPI({
                recovery_type: 'password',
                keyword: form.username,
                new_keyword: form.newPassword,
                confirm_new_keyword: form.newPassword,
                recovery_token: decodeToken(recovery_token as string),
            });
            if (isSuccess) {
                setSuccessMessage(t('Reset Successful!'));
                setShowAlert(true);
                await localforage.removeItem('recovery_token');
                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 2000);
                return;
            }
            if (status === 400) {
                setDisableUI(false);
                if (data?.need_reset) {
                    setErrorMessage(t('The recovery process took too long. Please log in again to start over.'));
                    setShowAlert(true);
                    setToggleExpiredDialog(true);
                    return;
                }
                setErrorMessage(message);
                setShowAlert(true);
                return;
            }
            throw new Error();
        } catch (error) {
            setDisableUI(false);
            setErrorMessage(t('Reset Password failed. Please try again.'));
            setShowAlert(true);
        } finally {
            hideLoading();
        }
    };

    // Validation
    const [focused, setFocused] = useState(false);
    const [pwdValidation, setPwdValidation] = useState({
        lower: false,
        upper: false,
        number: false,
        special: false,
        length: false,
        maxLength: true,
        noUsername: false,
    });
    const toggleResetDisable = useMemo(
        () => !form.username || !Object.values(pwdValidation).every((v) => v),
        [form.username, pwdValidation],
    );

    return (
        <div className="login-page">
            <BaseCard className="login-container">
                <div className="login-logo-container">
                    <img src={logo} alt={t('AURA Logo')} className="login-logo" />
                </div>

                <div className="login-header">
                    <span className="line"></span>
                    <h3 className="login-signin">{t('Reset Password')}</h3>
                    <span className="line"></span>
                </div>

                <div className="login-form">
                    <BaseTextField
                        autoComplete="new-username"
                        placeholder={t('Email/Username')}
                        startIcon={<AccountCircle />}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        value={form.username}
                        onChange={handleChange('username')}
                        onKeyUp={enterKeyHandler}
                        disabled={disableUI}
                    />
                    <PasswordField
                        placeholder={t('New Password')}
                        autoComplete="new-password"
                        startIcon={<Lock />}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        value={form.newPassword}
                        onChange={handleChange('newPassword')}
                        onKeyUp={enterKeyHandler}
                        disabled={disableUI}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        ariaLabelShow={t('Show password')}
                        ariaLabelHide={t('Hide password')}
                    />
                    <Collapse in={focused || !!form.newPassword} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, pt: 1 }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 14 }}>
                                {(
                                    [
                                        ['lower', t('One lowercase character')],
                                        ['upper', t('One uppercase character')],
                                        ['number', t('One number')],
                                        ['special', t('One special character')],
                                        ['length', t('8 characters minimum')],
                                        ['maxLength', t('70 characters maximum')],
                                        ['noUsername', t('Must not contain username')],
                                    ] as const
                                ).map(([key, label]) => (
                                    <li key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                        {pwdValidation[key] ? (
                                            <Check fontSize="small" color="success" />
                                        ) : (
                                            <Close fontSize="small" color="disabled" />
                                        )}
                                        <span style={{ marginLeft: 8, color: pwdValidation[key] ? 'inherit' : '#999' }}>
                                            {label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </Box>
                    </Collapse>
                    {showAlert && (
                        <BaseAlert
                            severity={errorMessage ? 'error' : 'success'}
                            message={errorMessage || successMessage || ''}
                        />
                    )}
                    <div className="button-group">
                        <BaseButton
                            fullWidth
                            className="reset-button"
                            startIcon={<LockResetIcon />}
                            color="secondary"
                            onClick={handleSubmit}
                            disabled={disableUI || toggleResetDisable}
                        >
                            {t('Reset Password')}
                        </BaseButton>
                    </div>
                </div>
            </BaseCard>
            <BaseDialog
                open={toggleExpiredDialog}
                dialogTitle={t('Session has expired')}
                confirmButtonText={t('OK')}
                onConfirm={() => {
                    navigate('/login', { replace: true });
                }}
                cancelButtonText=""
            >
                <Typography>{t('The recovery process took too long. Please log in again to start over.')}</Typography>
            </BaseDialog>
        </div>
    );
}
